import { Component, DestroyRef, OnInit, signal } from '@angular/core';
import { AdditionalInfoComponent } from '../shared/additional-info/additional-info.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { StudentService } from 'app/services/student/student.service';
import { AdditionalInfoService } from 'app/services/additional-info/additional-info.service';
import { BehaviorSubject, Observable, combineLatest, filter, firstValueFrom, map, mergeMap, tap } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { getNestedProperty, ObjectUtilities } from 'app/utilities/object-utilities/object-utilities';
import { StudentViewConfiguration } from './student-view.config';
import { Category, Field } from 'app/services/additional-info/additional-info.model';
import { TdoeBoolDisplayPipe } from 'app/pipes/bool-display/tdoe-bool-display.pipe';
import { MatTableModule } from '@angular/material/table';
import { LoggingService, TdoeAlertComponent, TdoeButtonDirective } from '@tdoe/design-system';
import { MatMenuModule } from '@angular/material/menu';
import { StringUtilities } from 'app/utilities/string-utilities/string-utilities';
import { downloadExcelDocument } from 'app/utilities/excel-export-utilities/excel-export-utilities';
import { Pagination, Sorting, Student } from 'app/dto';
import { NestedPropertyPipe } from 'app/pipes/nested-property/nested-property.pipe';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { JsonPathPipe } from 'app/pipes/json-path/json-path.pipe';

@Component({
  selector: 'app-student-view',
  standalone: true,
  imports: [
    AdditionalInfoComponent,
    MatExpansionModule,
    MatIconModule,
    CommonModule,
    MatTabsModule,
    RouterModule,
    MatTableModule,
    TdoeButtonDirective, 
    MatMenuModule,
    NestedPropertyPipe,
    NgxSkeletonLoaderModule,
    MatPaginatorModule,
    MatSortModule,
    TdoeAlertComponent,
    JsonPathPipe
],
  templateUrl: './student-view.component.html',
  styleUrl: './student-view.component.scss'
})
export class StudentViewComponent implements OnInit {

  protected readonly additionalInfoContextKey = 'StudentDetail';

  private readonly _categoriesSubject = new BehaviorSubject<Category[]>([]);
  private readonly _categories$: Observable<Category[]> = this._categoriesSubject.asObservable();

  private readonly _studentId = this.route.snapshot.paramMap.get('id') ?? '0';
  private readonly _student$ = firstValueFrom(this.studentService.getStudent(this._studentId));

  private readonly _studentCategories$: Observable<Category[]> =
    combineLatest([this._student$, this._categories$])
      .pipe(
        map(([student, categories]) => {
          return categories
            .filter(category => category.fields.some(field => field.selected))
            .map(category => ({
              ...category,
              fields: category.fields
                .filter(field => field.selected)
                .map(field => ({
                  ...field,
                  value: this.resolveNestedProperty(student, field)
                }))
            }));
        })
      );

  public additionalInfoFields: Category[] = StudentViewConfiguration;
    
  public state: { [k: string]: any; } | undefined;

  //#region Disciplinary actions

  public readonly disciplinaryActionsVM$ = combineLatest([
    this._student$
  ]).pipe(
    mergeMap(_ => this.studentService.getDisciplinaryActions(_[0].studentId!))
  );

  //#endregion

  //#region Active enrollment

  private readonly activeEnrollmentPaging = signal<Pagination>({ pageIndex: 0, pageSize: 50 });
  private readonly activeEnrollmentSorting = signal<Sorting>({ sortColumn: 'CourseCode', sortDirection: 'asc' });
  
  public readonly activeEnrollmentVM$ = combineLatest([
    toObservable(this.activeEnrollmentPaging),
    toObservable(this.activeEnrollmentSorting),
    this._student$
  ]).pipe(
    map(([
      paging,
      sorting,
      student
    ]) => ({
      paging,
      sorting,
      student
    })),
    mergeMap(_ => this.studentService.getEnrollmentInfo(_.student.studentId!))
  );

  //#endregion

  //#region Attendance

  private readonly attendancePaging = signal<Pagination>({ pageIndex: 0, pageSize: 50 });
  private readonly attendanceSorting = signal<Sorting>({ sortColumn: 'AttendanceDate', sortDirection: 'asc' });

  public readonly attendanceVM$ = combineLatest([
    toObservable(this.attendancePaging),
    toObservable(this.attendanceSorting),
    this._student$
  ]).pipe(
    map(([
      paging,
      sorting,
      student
    ]) => ({
      paging,
      sorting,
      student
    })),
    mergeMap(_ => this.studentService.getAttendance(_.student.studentId!, _.paging, _.sorting))
  );

  public pageAttendance(e: PageEvent): void {
    this.attendancePaging.set({
      pageIndex: e.pageIndex,
      pageSize: e.pageSize
    });
  }

  //#endregion

  //#region Club memberships

  public readonly clubMembershipVM$ = combineLatest([
    this._student$
  ]).pipe(
    mergeMap(_ => this.studentService.getClubMemberships(_[0].studentId!))
  );

  //#endregion

  //#region Final grades

  public readonly finalGradesVM$ = combineLatest([
    this._student$
  ]).pipe(
    mergeMap(_ => this.studentService.getFinalGrades(_[0].studentId!))
  );

  //#endregion

  protected viewModel$ = combineLatest([
    this._student$,
    this._studentCategories$
  ])
    .pipe(
      map(([student,
            studentCategories
          ]) => ({
            student,
            studentCategories
          })),
      map(data => ({
        student: data.student,
        studentCategories: data.studentCategories,
        staticSections: data.studentCategories.filter(_ => !_.noBinding),
        unboundCategories: data.studentCategories.filter(_ => _.noBinding),
        tableColumns: data.studentCategories.reduce<Record<string, string[]>>((acc, cat) => {
          if (cat.isArray) {
            acc[cat.name] = cat.fields.map(f => this.strip(f.key));
          }
          return acc;
        }, {}),
        attendanceColumns: this.getCategory(data.studentCategories, 'Attendance')?.fields.map(_ => _.key),
        attendanceCategory: this.getCategory(data.studentCategories, 'Attendance'),
        visibleCategories: {
          activeEnrollment: data.studentCategories.find(_ => _.key === 'activeEnrollment'),
          attendance: data.studentCategories.find(_ => _.key === 'attendance'),
          finalGrades: data.studentCategories.find(_ => _.key === 'finalGrades'),
          clubMemberships: data.studentCategories.find(_ => _.key === 'clubMemberships'),
          disciplinaryActions: data.studentCategories.find(_ => _.key === 'disciplinaryActions')
        },
        displayedColumns: {
          activeEnrollment: data.studentCategories.find(_ => _.key === 'activeEnrollment')?.fields.map(f => f.key),
          attendance: data.studentCategories.find(_ => _.key === 'attendance')?.fields.map(f => f.key),
          finalGrades: data.studentCategories.find(_ => _.key === 'finalGrades')?.fields.map(f => f.key),
          clubMemberships: data.studentCategories.find(_ => _.key === 'clubMemberships')?.fields.map(f => f.key),
          disciplinaryActions: data.studentCategories.find(_ => _.key === 'disciplinaryActions')?.fields.map(f => f.key)
        }
      })),
      tap(_ => {

        this.logger.debug('Mapping viewmodel...', { data: { _ } });
        // 
      })
    );

  private boolPipe = new TdoeBoolDisplayPipe();

  public constructor (
    private readonly route: ActivatedRoute,
    private readonly titleService: Title,
    private readonly studentService: StudentService,
    private readonly additionalInfoService: AdditionalInfoService,
    private readonly destroyReference: DestroyRef,
    readonly logger: LoggingService,
    readonly router: Router
  ) {
    this.state = router.getCurrentNavigation()?.extras.state;
  }
  
  public getArrayData(record: Student | undefined, path: string | undefined): unknown[] | undefined {
    if (!record || !path) return [];
    const data = ObjectUtilities.getNestedProperty(record, path);
    return Array.isArray(data) ? data : undefined;
  }
  
  public strip(val: string): string {
    return val.replace(/\./g, '_');
  }

  public getCategory(categories: Category[], categoryName: string): Category | undefined {
    return categories.find(_ => _.name === categoryName && _.fields.some(field => field.selected));
  }

  public isColumnVisible(categories: Category[], category: string, column: string): boolean {
    return this.getCategory(categories, category)?.fields.some(_ => _.key === column) ?? false;
  }

  public ngOnInit(): void {
    this.titleService.setTitle(`${this.titleService.getTitle()} - ${StringUtilities.FormatStringForUrl(this.route.snapshot.queryParams['name'])}`);
    
    //Clark remove subscribe from class file
    this.additionalInfoService.getFieldState(this.additionalInfoContextKey)
      .pipe(
        takeUntilDestroyed(this.destroyReference),
        filter(categories => !!categories)
      )
      .subscribe(categories => this._categoriesSubject.next(categories));
  }

  public onFieldSelected(categories: Category[]): void {
    console.debug('StudentViewComponent -> onFieldSelected', { categories });
    this._categoriesSubject.next(categories);
  }

  public resolveNestedProperty(student: unknown, col: Field): unknown {
    let result = getNestedProperty(student, col.valuePath ?? col.key);

    if (typeof result == 'boolean') {
      result = this.boolPipe.transform(result, 'Yes', 'No');
    }

    return result;
  }

  public async export(id: string, additonalInfo?: Category[]): Promise<void> {
    this.logger.debug('ClassViewComponent -> export', {
      data: {
        id,
        additonalInfo
      }
    });
    const file = await firstValueFrom(this.studentService.exportPdf(id, additonalInfo));
    downloadExcelDocument(file);
  }

}
